import * as React from 'react';

import styled from '@emotion/styled';
import StarIcon from '@mui/icons-material/Star';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { InstagramGradientIcon } from '../../../components/Icons/InstagramGradientIcon';
import InstagramTag from '../../../components/InstagramTag/InstagramTag';
import TikTokTag from '../../../components/TikTokTag/TikTokTag';
import { User } from '../../../graphql/API';
import Icons from '../../../themes/icons';
import { formatNumber } from '../../../utils/formatter';
import LinkProfile from './LinkProfile';
import ProfileCheckBox from './ProfileCheckBox';
import { OnChange } from './types';

type ProfileCardProps = {
  data: User;
  onChange: OnChange;
  fullProfile?: boolean;
  checked?: boolean;
  profileLinkDisabled?: boolean;
};

const ProfileCard: React.FC<ProfileCardProps> = ({
  data,
  fullProfile,
  onChange,
  checked,
  profileLinkDisabled = false,
}) => {
  return (
    <Box display='flex' flexDirection='column'>
      <ProfileCheckBox
        placeholderSrc={String(data.picture?.thumbnail)}
        avatarSrc={String(data.picture?.medium)}
        instagramVerified={Boolean(data.agencyProfileUrl)}
        lifeStyle={Boolean(data.lifestyle)}
        value={data.id}
        onChange={onChange}
        checked={checked}
        profileLinkDisabled={profileLinkDisabled}
      />
      <Typography
        component='div'
        variant='body2'
        fontWeight='bold'
        sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <LinkProfile
            url={`/users/${data.id}`}
            label={`${data.firstname} ${data.lastname}`}
            hoverTextDecoration='underline'
            showLink={!profileLinkDisabled}
          />
          {data.rating && data.rating > 0 ? (
            <Box
              component='span'
              sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '15px', lineHeight: 1 }}
            >
              <StarIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
              {data.rating.toFixed(1)}
            </Box>
          ) : null}
        </Box>

        <Stack
          direction='column'
          spacing={1}
          alignItems='flex-start'
          useFlexGap
          sx={{ mt: 1, fontWeight: 'normal' }}
        >
          <SocialCard>
            <InstagramGradientIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
            {fullProfile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <InstagramTag
                  username={data.instagramHandle}
                  showAtSign={false}
                  textColor='#000'
                  sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                />
                {(data.instagramFollowers ?? 0) > 0 && (
                  <Typography component='span' sx={{ fontSize: '0.875rem', color: '#000' }}>
                    · {formatNumber(data.instagramFollowers ?? 0)}
                  </Typography>
                )}
              </Box>
            )}
          </SocialCard>
          {data.tiktokHandle && (
            <SocialCard>
              <Icons.TiktokIcon width={10.61} height={13.43} style={{ objectFit: 'contain' }} />
              {fullProfile && (
                <TikTokTag
                  tiktokHandle={data.tiktokHandle}
                  showAtSign={false}
                  textColor='#000'
                  sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                />
              )}
            </SocialCard>
          )}
        </Stack>
      </Typography>
    </Box>
  );
};

const SocialCard = styled.div`
  display: inline-flex;
  padding: 4px 10px;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  background-color: #f5f5f5;
  border: 1px solid #efefef;
`;

export default ProfileCard;
